import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material";
import { useMemo, useState } from "react";

import type { AccountCode, BudgetLine, PartDecision } from "../api/types";
import { formatMoney, sumMoney } from "../lib/money";

/**
 * Ekran decyzji akceptanta — osobny od arkusza, i to jest rozstrzygnięcie.
 *
 * Producent tu **przegląda**, a nie edytuje: arkusz jest zbudowany pod
 * wpisywanie i wklejanie z Excela, więc te same komórki w roli listy
 * do zatwierdzenia zapraszają do przypadkowej zmiany kwoty zamiast decyzji
 * o niej.
 *
 * Decyzje na dwóch poziomach, bo tak wygląda praca: „cała postprodukcja
 * do przeliczenia" to jedno kliknięcie, a nie dwanaście. Decyzja o pozycji
 * nadpisuje decyzję o jej grupie, więc „cała ekipa do poprawy, ale gaffer
 * zostaje" da się wyrazić dwoma ruchami.
 *
 * Uzasadnienie jest **wspólne dla całej partii odrzuceń**. Producent zwykle
 * odrzuca kilka pozycji z jednego powodu („zmienił się plan zdjęciowy");
 * osobne pole przy każdej pozycji kosztowałoby więcej klikania, niż wnosi.
 * Gdy powody są różne, decyzje idą dwoma przebiegami.
 */

type Werdykt = "approve" | "reject" | null;

interface Props {
  open: boolean;
  lines: BudgetLine[];
  accountCodes: AccountCode[];
  onClose: () => void;
  onSubmit: (decisions: PartDecision[]) => void;
  pending?: boolean;
}

const STAN: Record<string, { label: string; color: "default" | "success" | "error" }> = {
  pending: { label: "czeka", color: "default" },
  approved: { label: "zatwierdzona", color: "success" },
  rejected: { label: "odrzucona", color: "error" },
};

export function PartialApproval({
  open, lines, accountCodes, onClose, onSubmit, pending,
}: Props) {
  const [wybory, setWybory] = useState<Record<string, Werdykt>>({});
  const [grupy, setGrupy] = useState<Record<string, Werdykt>>({});
  const [uzasadnienie, setUzasadnienie] = useState("");

  const konta = useMemo(() => {
    const mapa = new Map<string, AccountCode>();
    accountCodes.forEach((k) => mapa.set(k.code, k));
    return mapa;
  }, [accountCodes]);

  /**
   * Grupa pozycji to jej **konto nadrzędne**, nie własne.
   *
   * Pozycje kosztorysu wiszą na kontach liściastych, więc grupowanie po nich
   * dałoby dwadzieścia grup po jednym wierszu — i decyzja o grupie nie
   * różniłaby się niczym od decyzji o pozycji. Poziom wyżej odpowiada temu,
   * jak mówi produkcja: „cała ekipa zdjęciowa", „całe zdjęcia".
   */
  const grupaPozycji = (linia: BudgetLine): string => {
    const konto = konta.get(linia.account_code);
    const czesci = konto?.path?.split("/") ?? [];
    return czesci.length > 1 ? czesci[czesci.length - 2] : linia.account_code;
  };

  const nazwaGrupy = (kod: string) => konta.get(kod)?.name ?? "";

  /** Pozycje pogrupowane, w kolejności z kosztorysu. */
  const grupowane = useMemo(() => {
    const mapa = new Map<string, BudgetLine[]>();
    lines.forEach((linia) => {
      const kod = grupaPozycji(linia);
      const lista = mapa.get(kod) ?? [];
      lista.push(linia);
      mapa.set(kod, lista);
    });
    return [...mapa.entries()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, konta]);

  /** Co faktycznie zostanie wysłane — decyzja o pozycji bije decyzję o grupie. */
  const wypadkowa = (linia: BudgetLine): Werdykt =>
    wybory[String(linia.id)] ?? grupy[grupaPozycji(linia)] ?? null;

  const odrzucane = lines.filter((l) => wypadkowa(l) === "reject");
  const zatwierdzane = lines.filter((l) => wypadkowa(l) === "approve");
  const brakPowodu = odrzucane.length > 0 && !uzasadnienie.trim();
  const nicNieWybrano = odrzucane.length + zatwierdzane.length === 0;

  function wyslij() {
    const decyzje: PartDecision[] = [
      ...Object.entries(grupy)
        .filter(([, w]) => w)
        .map(([code, w]) => ({
          level: "group" as const, code, verdict: w as "approve" | "reject",
          comment: w === "reject" ? uzasadnienie.trim() : "",
        })),
      ...Object.entries(wybory)
        .filter(([, w]) => w)
        .map(([id, w]) => ({
          level: "line" as const, id, verdict: w as "approve" | "reject",
          comment: w === "reject" ? uzasadnienie.trim() : "",
        })),
    ];
    onSubmit(decyzje);
  }

  function zamknij() {
    setWybory({});
    setGrupy({});
    setUzasadnienie("");
    onClose();
  }

  return (
    <Dialog open={open} onClose={zamknij} fullWidth maxWidth="md">
      <DialogTitle>Decyzje o pozycjach kosztorysu</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="body2" color="text.secondary">
            Pozycje zatwierdzone są wiążące od razu — kierownik może na nich
            kontraktować, nie czekając na resztę kosztorysu. Reszta wraca
            do poprawy z uzasadnieniem.
          </Typography>

          {grupowane.map(([kod, pozycje]) => (
            <Box key={kod} sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}
                     sx={{ px: 1.5, py: 1, bgcolor: "action.hover" }}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  {kod} {nazwaGrupy(kod)}
                  <Typography component="span" variant="body2" color="text.secondary">
                    {" "}· {formatMoney(sumMoney(pozycje.map((p) => p.amount_net)))} zł
                  </Typography>
                </Typography>
                <ToggleButtonGroup
                  size="small" exclusive value={grupy[kod] ?? null}
                  onChange={(_, v) => setGrupy({ ...grupy, [kod]: v })}
                >
                  <ToggleButton value="approve" color="success">Cała grupa</ToggleButton>
                  <ToggleButton value="reject" color="error">Do poprawy</ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              {pozycje.map((linia) => {
                const wynik = wypadkowa(linia);
                const zGrupy = !wybory[String(linia.id)] && grupy[grupaPozycji(linia)];
                return (
                  <Stack key={String(linia.id)} direction="row" alignItems="center"
                         spacing={1} sx={{ px: 1.5, py: 0.75 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      <Typography component="span" variant="body2"
                                  color="text.secondary">
                        {linia.account_code}{" "}
                      </Typography>
                      {linia.description}
                    </Typography>
                    <Chip size="small" variant="outlined"
                          label={STAN[linia.approval_state ?? "pending"].label}
                          color={STAN[linia.approval_state ?? "pending"].color} />
                    <Typography variant="body2" sx={{ width: 110, textAlign: "right" }}>
                      {formatMoney(linia.amount_net)}
                    </Typography>
                    <ToggleButtonGroup
                      size="small" exclusive value={wynik}
                      onChange={(_, v) =>
                        setWybory({ ...wybory, [String(linia.id)]: v })}
                    >
                      <ToggleButton value="approve" color="success"
                                    sx={{ opacity: zGrupy ? 0.6 : 1 }}>OK</ToggleButton>
                      <ToggleButton value="reject" color="error"
                                    sx={{ opacity: zGrupy ? 0.6 : 1 }}>Popraw</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                );
              })}
            </Box>
          ))}

          {odrzucane.length > 0 && (
            <TextField
              label="Uzasadnienie odrzucenia" multiline minRows={2} required
              value={uzasadnienie} onChange={(e) => setUzasadnienie(e.target.value)}
              helperText={
                brakPowodu
                  ? "Bez powodu kierownik zgaduje, co poprawić — i odsyła to samo."
                  : `Trafi do wszystkich ${odrzucane.length} odrzuconych pozycji.`
              }
              error={brakPowodu}
            />
          )}

          <Alert severity="info" variant="outlined">
            Do zatwierdzenia: {zatwierdzane.length} poz. ·{" "}
            {formatMoney(sumMoney(zatwierdzane.map((l) => l.amount_net)))} zł.
            Do poprawy: {odrzucane.length} poz. ·{" "}
            {formatMoney(sumMoney(odrzucane.map((l) => l.amount_net)))} zł.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={zamknij}>Anuluj</Button>
        <Button variant="contained" onClick={wyslij}
                disabled={pending || nicNieWybrano || brakPowodu}>
          Zapisz decyzje
        </Button>
      </DialogActions>
    </Dialog>
  );
}
